import { useState, useEffect } from 'react';

const WAF_SCRIPT_ID = 'aws-waf-script';

export interface UseAwsWafOptions {
	/**
	 * AWS WAF application integration URL (challenge.js) for the current environment.
	 * Sourced from runtime content config. When empty/undefined the WAF SDK is not
	 * loaded and the hook reports ready so requests are not blocked.
	 */
	readonly challengeScriptUrl?: string;
	/**
	 * Domains the WAF token cookie should be attached to (frontend host + API domain).
	 * Sourced from runtime content config.
	 */
	readonly cookieDomainList?: readonly string[];
}

/**
 * Loads the AWS WAF challenge SDK and reports when the integration is ready.
 *
 * Driven entirely by runtime config rather than a hardcoded stage: if no
 * challenge script URL is configured for the environment, the SDK is skipped
 * and the hook reports ready (fail-open) so the app is never stuck behind a
 * missing/failed script. Cookie domains and the script source are
 * environment-specific and provided by the content service.
 */
export const useAwsWaf = ({ challengeScriptUrl, cookieDomainList }: UseAwsWafOptions): boolean => {
	const [isReady, setIsReady] = useState(!!window.AwsWafIntegration);

	useEffect(() => {
		// Already loaded by a previous mount or another consumer.
		if (window.AwsWafIntegration) {
			setIsReady(true);
			return undefined;
		}

		// WAF not configured for this environment: do not block requests.
		if (!challengeScriptUrl) {
			setIsReady(true);
			return undefined;
		}

		const markReady = () => setIsReady(true);

		const existingScript = document.getElementById(WAF_SCRIPT_ID);
		if (existingScript) {
			existingScript.addEventListener('load', markReady);
			existingScript.addEventListener('error', markReady);
			return () => {
				existingScript.removeEventListener('load', markReady);
				existingScript.removeEventListener('error', markReady);
			};
		}

		// Must be set before the SDK script executes.
		if (cookieDomainList && cookieDomainList.length > 0) {
			globalThis.awsWafCookieDomainList = [...cookieDomainList];
		}

		const script = document.createElement('script');
		script.id = WAF_SCRIPT_ID;
		script.src = challengeScriptUrl;
		script.type = 'text/javascript';
		script.async = true;
		script.onload = markReady;
		// Fail-open: if the SDK cannot load, let requests proceed rather than
		// leaving the UI stuck waiting for an integration that will never arrive.
		script.onerror = markReady;
		document.body.appendChild(script);
		return undefined;
	}, [challengeScriptUrl, cookieDomainList]);

	return isReady;
};
